import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Search, FileText, Image, File, Loader2, X, FolderOpen, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAM_TAGS = [
  { value: 'all', label: 'All' },
  { value: 'jobtalk', label: 'JobTalk' },
  { value: 'franchise', label: 'Franchise' },
];

const getFileIcon = (fileType) => {
  switch (fileType?.toUpperCase()) {
    case 'PDF':
      return <FileText className="h-5 w-5 text-red-500" />;
    case 'PNG':
    case 'JPG':
    case 'JPEG':
      return <Image className="h-5 w-5 text-blue-500" />;
    default:
      return <File className="h-5 w-5 text-slate-400" />;
  }
};

const getTeamBadge = (tags) => {
  if (!tags || tags.length === 0) return null;
  if (tags.includes('both')) {
    return <Badge className="bg-purple-100 text-purple-700 text-xs">Both</Badge>;
  }
  const tag = tags[0];
  return (
    <Badge className={tag === 'jobtalk' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-green-100 text-green-700 text-xs'}>
      {tag === 'jobtalk' ? 'JobTalk' : 'Franchise'}
    </Badge>
  );
};

export default function AttachFromLibraryModal({ 
  open, 
  onOpenChange, 
  onAttach,
  defaultTeamFilter = 'all',
  alreadyAttached = [] // Array of document IDs already attached
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState(defaultTeamFilter);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (open) {
      loadDocuments();
      setSelectedIds([]);
    }
  }, [open, teamFilter]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (teamFilter !== 'all') params.append('team_tag', teamFilter);
      params.append('limit', '500');
      
      const res = await axios.get(`${API}/documents?${params.toString()}`);
      setDocuments(res.data.items || []);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return documents;
    const q = searchQuery.toLowerCase();
    return documents.filter(doc => 
      doc.name.toLowerCase().includes(q) ||
      doc.file_name?.toLowerCase().includes(q)
    );
  }, [documents, searchQuery]);

  const toggleSelect = (docId) => {
    setSelectedIds(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleAttach = () => {
    const selectedDocs = documents.filter(d => selectedIds.includes(d.id));

    // Update UI immediately — don't block on the non-critical use-count calls
    onAttach(selectedDocs);
    onOpenChange(false);

    // Fire-and-forget use count increments in background
    selectedDocs.forEach(doc => {
      axios.post(`${API}/documents/${doc.id}/increment-use`).catch(() => {});
    });
  };

  const isAlreadyAttached = (docId) => alreadyAttached.includes(docId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col z-[200]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-indigo-600" />
            Attach from Document Library
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="flex items-center gap-3 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="w-32">
              <Tag className="h-4 w-4 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEAM_TAGS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Document List */}
        <div className="flex-1 overflow-y-auto border rounded-lg min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <FolderOpen className="h-10 w-10 mb-2" />
              <p>No documents found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredDocuments.map(doc => {
                const alreadyAdded = isAlreadyAttached(doc.id);
                const isSelected = selectedIds.includes(doc.id);
                
                return (
                  <label 
                    key={doc.id} 
                    className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                      alreadyAdded ? 'opacity-50 cursor-not-allowed' : ''
                    } ${isSelected ? 'bg-indigo-50' : ''}`}
                  >
                    <Checkbox 
                      checked={isSelected}
                      disabled={alreadyAdded}
                      onCheckedChange={() => !alreadyAdded && toggleSelect(doc.id)}
                    />
                    {getFileIcon(doc.file_type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 truncate">{doc.name}</span>
                        {getTeamBadge(doc.team_tags)}
                        {alreadyAdded && (
                          <Badge variant="outline" className="text-xs">Already attached</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {doc.file_type} • {doc.file_size_formatted}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected count */}
        {selectedIds.length > 0 && (
          <div className="text-sm text-indigo-600 font-medium">
            {selectedIds.length} document{selectedIds.length > 1 ? 's' : ''} selected
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAttach} disabled={selectedIds.length === 0}>
            Attach {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
